import React, { useState } from 'react';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  useToast,
  useColorModeValue,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Flex,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FaPaperPlane, FaEnvelope } from 'react-icons/fa';

// Backend API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website: '' // Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);
  
  const toast = useToast();
  
  // Move all color mode values to the top level
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const inputHoverBg = useColorModeValue('gray.100', 'gray.600');
  const successAlertBg = useColorModeValue('green.50', 'green.900');
  const successAlertColor = useColorModeValue('green.800', 'green.200');

  const baseUrl = "https://abhishekbhalerao.com";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  const validateForm = () => {
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return false;
    }
    
    if (!formData.email.trim()) {
      setFormError('Email is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormError('Please enter a valid email address');
      return false;
    }
    
    if (!formData.message.trim()) {
      setFormError('Message is required');
      return false;
    }
    
    if (formData.message.trim().length < 10) {
      setFormError('Message must be at least 10 characters long');
      return false;
    }
    
    setFormError('');
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Success
        setFormSuccess(true);
        setFormData({ name: '', email: '', message: '', website: '' });
        
        toast({
          title: 'Message sent!',
          description: "Thank you for reaching out. I'll get back to you as soon as possible.",
          status: 'info',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });
      } else {
        // API error
        setFormError(data.message || 'There was an error sending your message. Please try again.');
        
        toast({
          title: 'Error',
          description: data.message || 'There was an error sending your message. Please try again.',
          status: 'error',
          duration: 3000,
          position: 'top',
          isClosable: true,
        });
      }
    } catch (error) {
      // Network error
      setFormError('Unable to connect to the server. Please check your internet connection and try again.');
      
      toast({
        title: 'Connection Error',
        description: 'Unable to connect to the server. Please check your internet connection and try again.',
        status: 'error',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      <SEO 
        title="Contact | Abhishek Bhalerao - Full Stack Developer & Robotics Engineer"
        description="Get in touch with Abhishek Bhalerao for collaboration, project inquiries, or questions about full stack development, industrial automation, and robotics engineering."
        keywords="Contact, Abhishek Bhalerao, Full Stack Developer, Project Inquiry, Collaboration, Industrial Automation, Robotics Engineering, Hire Developer"
        ogImage={`${baseUrl}/images/contact_og.jpg`}
        twitterImage={`${baseUrl}/images/contact_og.jpg`}
        canonicalUrl={`${baseUrl}/contact`}
      />

      <Container maxW="container.xl" py={8} mt="120px">
        {/* Header Section */}
        <Box mb={12} textAlign="center">
          <Heading as="h1" size="2xl" mb={4} color={headingColor}>
            Get In Touch
          </Heading>
          
          <Divider my={6} maxW="100px" borderWidth="2px" borderColor="blue.500" mx="auto" />
          
          <Text 
            fontSize="lg" 
            textAlign="center" 
            mb={10} 
            maxW="800px" 
            mx="auto"
            color={accentColor}
          >
            Have a project in mind or want to discuss a collaboration? I'd love to hear from you.
            Fill out the form below and I'll get back to you as soon as possible.
          </Text>
        </Box>
        
        {/* Contact Form - Now centered */}
        <Box
          p={8}
          borderRadius="lg"
          bg={bgColor}
          boxShadow="md"
          borderWidth="1px"
          borderColor={borderColor}
          maxW="800px"
          mx="auto"
          mb={16}
        >
          {formSuccess ? (
            <Alert
              status="success"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              height="300px"
              borderRadius="md"
              bg={successAlertBg}
              color={successAlertColor}
            >
              <Icon as={FaPaperPlane} boxSize="50px" color="green.500" mb={4} />
              <AlertTitle mt={4} mb={1} fontSize="xl" fontWeight="bold">
                Message Sent Successfully!
              </AlertTitle>
              <AlertDescription maxWidth="sm" fontSize="md" my={4}>
                Thank you for reaching out. I'll get back to you as soon as possible.
              </AlertDescription>
              <Button 
                mt={4} 
                colorScheme="blue" 
                size="lg"
                onClick={() => setFormSuccess(false)}
                boxShadow="md"
              >
                Send Another Message
              </Button>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Heading as="h3" size="md" mb={6} color={headingColor}>
                Send Me a Message
              </Heading>
              
              {formError && (
                <Alert status="error" mb={6} borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>{formError}</AlertDescription>
                  <CloseButton 
                    position="absolute" 
                    right="8px" 
                    top="8px" 
                    onClick={() => setFormError('')}
                  />
                </Alert>
              )}
              
              {/* Honeypot field - hidden from users but bots will fill it out */}
              <Input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                display="none"
                aria-hidden="true"
                tabIndex="-1"
                autoComplete="off"
              />
              
              <VStack spacing={5}>
                <FormControl isRequired>
                  <FormLabel fontWeight="medium">Name</FormLabel>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    focusBorderColor="blue.500"
                    size="lg"
                    bg={subtleBg}
                    _hover={{ bg: inputHoverBg }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontWeight="medium">Email</FormLabel>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your email address"
                    focusBorderColor="blue.500"
                    size="lg"
                    bg={subtleBg}
                    _hover={{ bg: inputHoverBg }}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel fontWeight="medium">Message</FormLabel>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message"
                    focusBorderColor="blue.500"
                    rows={6}
                    size="lg"
                    bg={subtleBg}
                    _hover={{ bg: inputHoverBg }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  width="100%"
                  mt={4}
                  isLoading={isSubmitting}
                  loadingText="Sending..."
                  rightIcon={<FaPaperPlane />}
                  boxShadow="md"
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                  transition="all 0.2s"
                >
                  Send Message
                </Button>
              </VStack>
            </form>
          )}
        </Box>
        
        {/* Call to Action */}
        <Box
          mt={16}
          position="relative"
          overflow="hidden"
          borderRadius="lg"
          boxShadow="xl"
        >
          {/* Background with gradient and pattern */}
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="blue.600"
            backgroundImage="linear-gradient(135deg, #3182CE 0%, #2C5282 100%)"
            zIndex="0"
          />
          
          {/* Decorative elements */}
          <Box
            position="absolute"
            top="-20px"
            right="-20px"
            width="150px"
            height="150px"
            borderRadius="full"
            bg="whiteAlpha.200"
            zIndex="0"
          />
          <Box
            position="absolute"
            bottom="-30px"
            left="-30px"
            width="120px"
            height="120px"
            borderRadius="full"
            bg="whiteAlpha.100"
            zIndex="0"
          />
          
          {/* Content */}
          <Flex
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="space-between"
            p={{ base: 8, md: 10 }}
            position="relative"
            zIndex="1"
          >
            <Box maxW={{ base: "100%", md: "60%" }} mb={{ base: 6, md: 0 }}>
              <Heading 
                as="h2" 
                size="lg" 
                color="white" 
                mb={4}
                fontWeight="bold"
              >
                Have a Project in Mind?
              </Heading>
              <Text color="whiteAlpha.900" fontSize="md" maxW="500px">
                From concept to completion, I'm here to help with your software development, automation, 
                or robotics engineering needs. Let's discuss how we can make your project successful.
              </Text>
            </Box>
            
            <Box>
              <Button
                as="a"
                href="mailto:contact@abhishekbhalerao.com"
                size="lg"
                bg="white"
                color="blue.600"
                _hover={{ bg: "gray.100" }}
                rightIcon={<FaEnvelope />}
                boxShadow="md"
                fontWeight="medium"
                px={8}
              >
                Email Me Directly
              </Button>
            </Box>
          </Flex>
        </Box>
      </Container>
    </>
  );
};

export default Contact;








